<template>
  <div class="filter-dropdown">
    <div v-if="isOpen" class="dropdown-overlay" @click="closeDropdown"></div>
    <button
      v-if="title"
      class="filter-dropdown-toggle"
      type="button"
      :aria-expanded="isOpen"
      :style="{ color: color }"
      @click="toggleDropdown">
      <component :is="icon" v-if="icon" />
      {{ title }}
    </button>

    <button
      v-else
      class="filter-dropdown-toggle"
      type="button"
      :aria-expanded="isOpen"
      :style="{ color: multiple ? color : selectedOption?.color }"
      @click="toggleDropdown">
      <component :is="multiple ? icon : selectedOption?.icon" v-if="multiple ? icon : selectedOption?.icon" />
      <template v-if="multiple">
        {{ getMultipleSelectionLabel() }}
      </template>
      <template v-else>
        {{ selectedOption?.label }}
      </template>
      <span class="arrow" :class="{ open: isOpen }">▼</span>
    </button>

    <ul v-if="isOpen" class="filter-dropdown-menu">
      <li
        v-for="option in options"
        :key="option.id"
        :class="{ active: option.value === localSelectedOption?.value }"
        @click="selectOption(option)">
        <p :style="{ color: option.color }" class="filter-option clickable-option">
          <component :is="option.icon" v-if="option.icon" class="option-icon" />
          <i>{{ option.label }}</i>
        </p>
      </li>
    </ul>

    <ul v-if="isOpen && multiple" class="filter-dropdown-menu">
      <li v-for="group in options" :key="group.title">
        <p class="filter-title">
          {{ group.title }}
        </p>
        <p
          v-for="option in group.options"
          :key="option.id"
          :style="{ color: option.color }"
          :class="{ active: isOptionSelected(group.title, option.id) }"
          class="filter-option clickable-option multiple-option"
          @click="selectMultipleOption(group.title, option)">
          <component :is="option.icon" v-if="option.icon" class="option-icon" />
          <i>{{ option.label }}</i>
        </p>
      </li>
    </ul>
  </div>
</template>

<script>
import { usePeopleStore } from '@/store/peopleStore';

export default {
  name: 'SortDropdownItem',
  props: {
    options: {
      type: Array,
      required: true,
    },
    select: {
      type: Function,
      required: true,
    },
    selectedOption: {
      type: Object || null,
      required: false,
      default: null,
    },
    title: {
      type: String || null,
      required: false,
      default: null,
    },
    color: {
      type: String,
      required: false,
      default: 'var(--text)',
    },
    icon: {
      type: Object || null,
      required: false,
      default: null,
    },
    multiple: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup() {
    const peopleStore = usePeopleStore();
    return { peopleStore };
  },
  data() {
    return {
      isOpen: false,
      localSelectedOption: this.selectedOption,
      multipleSelections: {},
    };
  },
  watch: {
    selectedOption(newVal) {
      this.localSelectedOption = newVal;
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    toggleDropdown() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.$nextTick(() => {
          this.adjustDropdownPosition();
        });
      }
    },
    closeDropdown() {
      this.isOpen = false;
    },
    adjustDropdownPosition() {
      const dropdown = this.$el.querySelector('.filter-dropdown-menu');
      if (!dropdown) return;

      // Retire les classes existantes
      dropdown.classList.remove('dropdown-above', 'dropdown-mobile');

      // Si écran mobile (moins de 600px), utiliser le menu du bas
      if (window.innerWidth < 600) {
        dropdown.classList.add('dropdown-mobile');
        return;
      }

      const rect = this.$el.getBoundingClientRect();
      const dropdownHeight = dropdown.offsetHeight;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Si pas assez de place en dessous et plus de place au-dessus
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        dropdown.classList.add('dropdown-above');
      }
    },
    selectOption(val) {
      this.localSelectedOption = val;
      this.isOpen = false;
      if (this.$props.select) {
        this.$props.select(val);
      }
    },
    selectMultipleOption(groupTitle, option) {
      this.isOpen = false;
      this.multipleSelections[groupTitle] = option;
      if (this.$props.select) {
        this.$props.select(option, groupTitle);
      }
    },
    isOptionSelected(groupTitle, optionValue) {
      return this.multipleSelections[groupTitle]?.value === optionValue;
    },
    getMultipleSelectionLabel() {
      const count = Object.keys(this.multipleSelections).length;
      if (count === 0) {
        return this.title || this.$t('dropdown.select');
      }
      if (count === 1) {
        return Object.values(this.multipleSelections)[0].label;
      }
      return `${count} ${this.$t('dropdown.selected')}`;
    },
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.isOpen = false;
      }
      if (this.$props.multiple) {
        this.$props.select(Object.values(this.multipleSelections).map((opt) => opt.value));
      }
    },
  },
};
</script>

<style scoped>
.filter-dropdown {
  position: relative;
  display: inline-block;
}

.dropdown-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: var(--half-transparent);
  z-index: 9;
}

.filter-dropdown-toggle {
  border: none;
  background: var(--transparent);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.filter-dropdown-toggle .arrow {
  margin-left: 0.5em;
  transition: transform 0.2s;
  font-size: 10px;
}

.filter-dropdown-toggle .arrow.open {
  transform: rotate(180deg);
}

.filter-dropdown-menu {
  position: absolute;
  left: 0;
  top: 100%;
  z-index: 11;
  background: var(--bg-secondary);
  border-radius: 20px;
  width: 200px;
  margin-top: 0.25em;
  box-shadow: 0 2px 8px var(--bg-secondary);
  list-style: none;
  padding: 0;
  max-height: 50vh;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--text-secondary) var(--bg-tertiary);
}

.filter-dropdown-menu::-webkit-scrollbar {
  width: 6px;
}

.filter-dropdown-menu::-webkit-scrollbar-track {
  background: var(--bg-tertiary);
  border-radius: 3px;
}

.filter-dropdown-menu::-webkit-scrollbar-thumb {
  background: var(--text-secondary);
  border-radius: 3px;
}

@media (hover: hover) {
  .filter-dropdown-menu::-webkit-scrollbar-thumb:hover {
    background: var(--text);
  }

  .clickable-option:hover {
    background: var(--bg-tertiary);
    color: var(--text-tertiary);
  }
}

.active {
  background: var(--bg-primary);
  color: var(--text-primary);
}

.filter-title {
  font-weight: bold;
  padding: 15px;
  border-bottom: 1px solid var(--border-secondary);
}

.filter-dropdown-menu.dropdown-above {
  top: auto;
  bottom: 100%;
  margin-top: 0;
  margin-bottom: 0.25em;
}

.filter-dropdown-menu.dropdown-mobile {
  position: fixed;
  left: 0;
  top: auto;
  bottom: 0;
  width: 100vw;
  margin: 0;
  border-radius: 20px 20px 0 0;
  max-height: 70vh;
  overflow-y: auto;
}

.filter-dropdown-menu li {
  cursor: pointer;
  transition: background 0.2s;
}

.filter-option {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  border-bottom: 1px solid var(--border-secondary);
  padding: 15px;
}

.multiple-option {
  padding-left: 30px;
}

.filter-dropdown-menu li:last-child .filter-option {
  border-bottom: none;
}

.option-icon {
  width: 20px;
  height: 20px;
}
</style>
